import { supabase } from "../supabase/supabase";

interface CsvLeadRow {
  first_name?: string;
  last_name?: string;
  email?: string;
  country_code?: string;
  phone_number?: string;
  [key: string]: any;
}

interface BulkImportResult {
  total: number;
  successful: number;
  failed: number;
  errors: Array<{ row: number; error: string }>;
}

export const csvLeadService = {
  /**
   * Validate a single lead row
   */
  validateLeadRow(
    row: CsvLeadRow,
    rowIndex: number
  ): { isValid: boolean; error?: string; validatedRow?: any } {
    const errors: string[] = [];

    // Extract and validate fields
    let firstName = row.first_name?.trim() || "";
    let lastName = row.last_name?.trim() || "";
    const email = row.email?.trim() || "";
    const countryCode = row.country_code?.trim() || "";
    const phoneNumber = row.phone_number?.trim() || "";

    // If no first_name or last_name, try to extract from a combined name field
    if (!firstName && !lastName && row.name) {
      const nameParts = row.name.trim().split(/\s+/);
      firstName = nameParts[0] || "";
      lastName = nameParts.slice(1).join(" ") || "";
    }

    // Validate required fields - all are mandatory
    if (!firstName) {
      errors.push("First name is required");
      return {
        isValid: false,
        error: errors.join(", "),
      };
    }

    if (!lastName) {
      errors.push("Last name is required");
      return {
        isValid: false,
        error: errors.join(", "),
      };
    }

    // Country code is required
    if (!countryCode) {
      errors.push("Country code is required");
      return {
        isValid: false,
        error: errors.join(", "),
      };
    }

    // Validate country code format (must start with +)
    if (!countryCode.startsWith("+")) {
      errors.push("Country code must start with + (e.g., +91, +1, +971)");
      return {
        isValid: false,
        error: errors.join(", "),
      };
    }

    // Validate country code contains only digits after +
    const countryCodeDigits = countryCode.slice(1); // Remove the +
    if (!/^\d+$/.test(countryCodeDigits)) {
      errors.push("Country code must contain only digits after + (e.g., +91, not +91a)");
      return {
        isValid: false,
        error: errors.join(", "),
      };
    }

    // Phone number is required (database constraint)
    if (!phoneNumber) {
      errors.push("Phone number is required");
      return {
        isValid: false,
        error: errors.join(", "),
      };
    }

    // Validate phone number based on country code
    const digitsOnly = phoneNumber.replace(/\D/g, "");

    switch (countryCode) {
      case "+91": // India
        if (digitsOnly.length !== 10) {
          errors.push("Indian phone numbers must be exactly 10 digits");
          return { isValid: false, error: errors.join(", ") };
        }
        break;

      case "+1": // US/Canada
        if (digitsOnly.length !== 10) {
          errors.push("US/Canada phone numbers must be exactly 10 digits");
          return { isValid: false, error: errors.join(", ") };
        }
        break;

      case "+44": // UK
        if (digitsOnly.length < 10 || digitsOnly.length > 11) {
          errors.push("UK phone numbers must be 10-11 digits");
          return { isValid: false, error: errors.join(", ") };
        }
        break;

      case "+971": // UAE
        if (digitsOnly.length !== 9) {
          errors.push("UAE phone numbers must be exactly 9 digits");
          return { isValid: false, error: errors.join(", ") };
        }
        break;

      default:
        // For other countries, accept 8-15 digits
        if (digitsOnly.length < 8 || digitsOnly.length > 15) {
          errors.push(`Phone numbers for ${countryCode} must be 8-15 digits`);
          return { isValid: false, error: errors.join(", ") };
        }
    }

    // Validate email format if provided
    if (email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        errors.push("Invalid email format");
        return {
          isValid: false,
          error: errors.join(", "),
        };
      }
    }

    // Combine country code with phone number for storage
    const fullPhoneNumber = phoneNumber ? `${countryCode}${phoneNumber}` : null;

    return {
      isValid: true,
      validatedRow: {
        first_name: firstName,
        last_name: lastName,
        email: email || null,
        phone_number: fullPhoneNumber, // Store as +919876543210
      },
    };
  },

  /**
   * Bulk create leads from CSV data
   */
  async bulkCreateLeads(
    rows: CsvLeadRow[],
    organizationId: string,
    campaignId?: string,
    onProgress?: (current: number, total: number) => void,
    fileName?: string,
    callflowId?: string
  ): Promise<BulkImportResult> {
    console.log('=== CSV BULK CREATE DEBUG ===');
    console.log('organizationId:', organizationId);
    console.log('campaignId:', campaignId);
    console.log('callflowId:', callflowId);
    console.log('fileName:', fileName);
    console.log('rows count:', rows.length);
    const result: BulkImportResult = {
      total: rows.length,
      successful: 0,
      failed: 0,
      errors: [],
    };

    // Create a lead_upload record to track this CSV import
    let leadUploadId: string | null = null;
    try {
      const { data: uploadRecord, error: uploadError } = await supabase
        .from("lead_uploads")
        .insert({
          organization_id: organizationId,
          original_filename: fileName || "csv_import.csv",
          file_path: `csv_imports/${organizationId}/${Date.now()}.csv`,
          upload_status: "processing",
          campaign_id: campaignId || null,
        })
        .select()
        .single();

      if (uploadError) {
        console.error("Error creating lead_upload record:", uploadError);
      } else if (uploadRecord) {
        leadUploadId = uploadRecord.id;
        console.log("Created lead_upload record:", leadUploadId);
      }
    } catch (error) {
      console.error("Exception creating lead_upload record:", error);
    }

    const validatedLeads: any[] = [];
    const validatedIndexes: number[] = [];

    // Validate all rows first
    console.log(`Validating ${rows.length} CSV rows...`);
    for (let i = 0; i < rows.length; i++) {
      const validation = this.validateLeadRow(rows[i], i + 1);

      if (validation.isValid && validation.validatedRow) {
        validatedLeads.push({
          ...validation.validatedRow,
          organization_id: organizationId,
        });
        validatedIndexes.push(i + 1);
      } else {
        result.failed++;
        result.errors.push({
          row: i + 1,
          error: validation.error || "Validation failed",
        });
      }
    }

    console.log(`Validation complete: ${validatedLeads.length} valid, ${result.failed} invalid`);

    if (validatedLeads.length === 0) {
      console.log("No valid leads to import");
      return result;
    }

    // Import leads in batches of 50
    const BATCH_SIZE = 50;
    const batches = [];
    for (let i = 0; i < validatedLeads.length; i += BATCH_SIZE) {
      batches.push(validatedLeads.slice(i, i + BATCH_SIZE));
    }

    console.log(`Importing ${validatedLeads.length} leads in ${batches.length} batches...`);

    // Process each batch
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const batchStartIndex = batchIndex * BATCH_SIZE;

      try {
        // Check for existing leads with same phone numbers in this organization
        const phoneNumbers = batch
          .map((lead) => lead.phone_number)
          .filter(Boolean);

        let existingLeads: any[] = [];
        if (phoneNumbers.length > 0) {
          const { data: existing } = await supabase
            .from("leads")
            .select("id, phone_number")
            .eq("organization_id", organizationId)
            .in("phone_number", phoneNumbers);

          existingLeads = existing || [];
        }

        const existingPhoneMap = new Map(
          existingLeads.map((lead) => [lead.phone_number, lead.id])
        );

        // Separate into updates and inserts
        const leadsToInsert: any[] = [];
        const leadsToUpdate: Array<{ id: string; data: any }> = [];

        batch.forEach((lead, idx) => {
          if (lead.phone_number && existingPhoneMap.has(lead.phone_number)) {
            // Update existing lead
            leadsToUpdate.push({
              id: existingPhoneMap.get(lead.phone_number)!,
              data: {
                first_name: lead.first_name,
                last_name: lead.last_name,
                email: lead.email,
                updated_at: new Date().toISOString(),
              },
            });
          } else {
            // Insert new lead
            leadsToInsert.push(lead);
          }
        });

        // Insert new leads
        let insertedLeads: any[] = [];
        if (leadsToInsert.length > 0) {
          console.log(`Batch ${batchIndex + 1}: Inserting ${leadsToInsert.length} new leads`);
          const { data: inserted, error: insertError } = await supabase
            .from("leads")
            .insert(leadsToInsert)
            .select();

          if (insertError) {
            console.error("Batch insert error:", insertError);
            // Mark all as failed for this batch
            batch.forEach((_, idx) => {
              result.failed++;
              result.errors.push({
                row: validatedIndexes[batchStartIndex + idx],
                error: insertError.message,
              });
            });
            continue;
          }

          insertedLeads = inserted || [];
        }

        // Update existing leads and collect them for callflow addition
        let updatedLeads: any[] = [];
        if (leadsToUpdate.length > 0) {
          console.log(`Batch ${batchIndex + 1}: Updating ${leadsToUpdate.length} existing leads`);
          for (const update of leadsToUpdate) {
            const { error: updateError } = await supabase
              .from("leads")
              .update(update.data)
              .eq("id", update.id);

            if (updateError) {
              console.error("Update error:", updateError);
              result.failed++;
              result.errors.push({
                row: validatedIndexes[batchStartIndex],
                error: `Update failed: ${updateError.message}`,
              });
            } else {
              result.successful++;
              // Add updated lead to the list for callflow addition
              updatedLeads.push({ id: update.id });
            }
          }
        }

        // Add inserted leads to campaign if campaignId is provided
        if (campaignId && insertedLeads.length > 0) {
          console.log(`Adding ${insertedLeads.length} leads to campaign ${campaignId}`);
          const campaignLeads = insertedLeads.map((lead) => ({
            campaign_id: campaignId,
            lead_id: lead.id,
          }));

          const { error: campaignError } = await supabase
            .from("campaign_leads")
            .insert(campaignLeads);

          if (campaignError) {
            console.error("Error adding leads to campaign:", campaignError);
            // Don't fail the import, just log the error
          }
        }

        // Add all processed leads (inserted + updated) to callflow if callflowId is provided
        const allProcessedLeads = [...insertedLeads, ...updatedLeads];
        if (callflowId && allProcessedLeads.length > 0) {
          console.log(`=== CALLFLOW INSERTION DEBUG ===`);
          console.log(`callflowId:`, callflowId);
          console.log(`insertedLeads count:`, insertedLeads.length);
          console.log(`updatedLeads count:`, updatedLeads.length);
          console.log(`total leads to add to callflow:`, allProcessedLeads.length);
          
          const callflowLeads = allProcessedLeads.map((lead) => ({
            callflow_id: callflowId,
            lead_id: lead.id,
            status: 'pending',
            attempts: 0,
            added_at: new Date().toISOString(),
          }));

          console.log(`Callflow leads to insert:`, callflowLeads);

          const { data: callflowInsertData, error: callflowError } = await supabase
            .from("callflow_leads")
            .insert(callflowLeads)
            .select();

          if (callflowError) {
            console.error("=== CALLFLOW INSERT ERROR ===");
            console.error("Error details:", callflowError);
            console.error("Error message:", callflowError.message);
            console.error("Error code:", callflowError.code);
            console.error("Error hint:", callflowError.hint);
          } else {
            console.log("=== CALLFLOW INSERT SUCCESS ===");
            console.log("Inserted callflow leads:", callflowInsertData);
          }
        } else {
          console.log(`=== CALLFLOW INSERTION SKIPPED ===`);
          console.log(`callflowId:`, callflowId);
          console.log(`insertedLeads.length:`, insertedLeads?.length || 0);
          console.log(`updatedLeads.length:`, updatedLeads?.length || 0);
        }

        result.successful += insertedLeads.length;

        // Update progress
        if (onProgress) {
          const processed = Math.min(
            (batchIndex + 1) * BATCH_SIZE,
            validatedLeads.length
          );
          onProgress(processed, validatedLeads.length);
        }
      } catch (error) {
        console.error(`Error processing batch ${batchIndex + 1}:`, error);
        // Mark this batch as failed
        batch.forEach((_, idx) => {
          result.failed++;
          result.errors.push({
            row: validatedIndexes[batchStartIndex + idx],
            error:
              error instanceof Error ? error.message : "Unknown error",
          });
        });
      }
    }

    console.log("Import complete:", result);

    // Update lead_upload record status
    if (leadUploadId) {
      try {
        const finalStatus =
          result.failed === 0
            ? "completed"
            : result.successful > 0
            ? "completed"
            : "failed";

        await supabase
          .from("lead_uploads")
          .update({
            upload_status: finalStatus,
            updated_at: new Date().toISOString(),
          })
          .eq("id", leadUploadId);

        console.log(`Updated lead_upload ${leadUploadId} status to ${finalStatus}`);
      } catch (error) {
        console.error("Error updating lead_upload status:", error);
      }
    }

    return result;
  },

  /**
   * Download a CSV template
   */
  downloadTemplate(): void {
    const headers = ["first_name", "last_name", "email", "country_code", "phone_number"];
    const exampleRow = [
      "John",
      "Doe",
      "john.doe@example.com",
      "+91",
      "9876543210",
    ];

    const csv = [headers.join(","), exampleRow.join(",")].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "lead_import_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
